import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useMyOrgs } from '../data/entities';
import { apiGet, backendBase } from '../utils/backendApi';
import PageWorld from '../components/site/PageWorld';
import { SUPPORT } from '../data/worlds';

const OPEN_KEY = 'nv-support-open';

function Field({ label, children }) {
  return (
    <div className="cs-field">
      <label>{label}</label>
      {children}
    </div>
  );
}

function Answer({ ticket, slow }) {
  if (!ticket) return <p className="cs-help">Sending…</p>;
  if (!slow && (ticket.status === 'new' || ticket.status === 'queued' || !ticket.status)) {
    return <p className="cs-help">Looking through what nventr already knows…</p>;
  }
  return (
    <div className="cs-support-answer">
      {ticket.answer ? (
        <>
          <h3>What we know</h3>
          <p style={{ whiteSpace: 'pre-wrap' }}>{ticket.answer}</p>
          {ticket.citations?.length > 0 && (
            <p className="cs-help">
              Sources: {ticket.citations.map((c) => c.title).join(', ')}
            </p>
          )}
          <p className="cs-help">A person on the team has this as well{ticket.topicLabel ? ` (${ticket.topicLabel})` : ''}.</p>
        </>
      ) : (
        <>
          <h3>We have it</h3>
          <p>Nothing already written answers this. A person on the team will reply to {ticket.name ? 'you' : 'your email'}.</p>
        </>
      )}
    </div>
  );
}

export default function Support() {
  const { user } = useAuth();
  const member = !!user && !user.isAnonymous;
  const orgs = useMyOrgs();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: params.get('subject') || '',
    message: '',
    orgId: '',
  });
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ticket, setTicket] = useState(null);
  const [slow, setSlow] = useState(false);
  const [mine, setMine] = useState(null);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  useEffect(() => {
    if (!member) return;
    setForm((f) => ({
      ...f,
      name: f.name || user.displayName || '',
      email: user.email || f.email,
    }));
  }, [member, user]);

  useEffect(() => {
    const raw = sessionStorage.getItem(OPEN_KEY);
    if (!raw) return;
    try {
      const saved = JSON.parse(raw);
      if (saved?.id && saved?.token) setTicket(saved);
    } catch { /* ignore a bad cache */ }
  }, []);

  useEffect(() => {
    if (!ticket?.id || !ticket?.token) return undefined;
    if (ticket.answer || (ticket.status && ticket.status !== 'new' && ticket.status !== 'queued')) return undefined;
    let stop = false;
    const pull = async () => {
      const base = backendBase();
      if (!base) return;
      try {
        const res = await fetch(`${base}/support/tickets/${ticket.id}?token=${encodeURIComponent(ticket.token)}`);
        const data = await res.json().catch(() => ({}));
        if (!stop && res.ok) setTicket((t) => ({ ...t, ...data }));
      } catch { /* the next tick retries */ }
    };
    pull();
    const timer = setInterval(pull, 2000);
    const giveUp = setTimeout(() => setSlow(true), 25000);
    return () => { stop = true; clearInterval(timer); clearTimeout(giveUp); };
  }, [ticket?.id, ticket?.token, ticket?.status, ticket?.answer]);

  useEffect(() => {
    if (!member) { setMine(null); return undefined; }
    let stop = false;
    apiGet('/support/mine').then((data) => { if (!stop) setMine(data.tickets || []); }).catch(() => { if (!stop) setMine([]); });
    return () => { stop = true; };
  }, [member, ticket?.status]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const base = backendBase();
    if (!base) {
      setError('The desk is unavailable right now. Try again in a minute.');
      return;
    }
    setBusy(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (member) headers.Authorization = `Bearer ${await user.getIdToken()}`;
      const res = await fetch(`${base}/support/tickets`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...form, website }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not send that.');
      const next = { id: data.id, token: data.token, status: 'new', name: form.name, email: member ? user.email : form.email };
      sessionStorage.setItem(OPEN_KEY, JSON.stringify(next));
      setSlow(false);
      setTicket(next);
    } catch (err) {
      setError(err.message || 'Could not send that.');
    } finally {
      setBusy(false);
    }
  };

  const another = () => {
    sessionStorage.removeItem(OPEN_KEY);
    setTicket(null);
    setForm((f) => ({ ...f, subject: '', message: '' }));
  };

  return (
    <PageWorld world={SUPPORT}>
    <div className="cs-store">
      <header className="cs-store-hero">
        <h1>Support</h1>
        <p>
          {member
            ? 'Send a request from your account. We answer from what nventr knows, and the right person on the team is notified.'
            : 'Send a request. We answer from what nventr knows, and the right person on the team is notified.'}
        </p>
      </header>
      <section className="cs-store-section cs-contact">
        {ticket ? (
          <div className="cs-sw-card">
            <h3>Request sent</h3>
            <p>We’ll also write to {ticket.email || form.email} when there is a cited answer.</p>
            <Answer ticket={ticket} slow={slow} />
            <button className="cs-btn cs-btn-outline" type="button" onClick={another} style={{ marginTop: 16 }}>
              Send another
            </button>
          </div>
        ) : (
          <form className="cs-sw-card cs-contact-form" onSubmit={submit}>
            {!member && (
              <p className="cs-help" style={{ marginTop: 0 }}>
                Have an account? <Link to="/login?next=/support">Sign in</Link> and we can use your organization.
              </p>
            )}
            <Field label="Name">
              <input className="cs-input" required value={form.name} onChange={set('name')} autoComplete="name" />
            </Field>
            <Field label="Email">
              <input className="cs-input" required type="email" value={member ? (user.email || '') : form.email} onChange={set('email')} autoComplete="email" readOnly={member} />
            </Field>
            {member && (orgs || []).length > 0 && (
              <Field label="About this organization">
                <select className="cs-input" value={form.orgId} onChange={set('orgId')}>
                  <option value="">General — public answers only</option>
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </Field>
            )}
            <Field label="Subject">
              <input className="cs-input" value={form.subject} onChange={set('subject')} autoComplete="off" />
            </Field>
            <Field label="What do you need?">
              <textarea className="cs-input" required rows={6} minLength={8} value={form.message} onChange={set('message')} />
            </Field>
            <div className="cs-hp" aria-hidden="true">
              <label>
                Website
                <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
              </label>
            </div>
            {error && <p className="cs-land-micro">{error}</p>}
            <button className="cs-btn cs-btn-primary" type="submit" disabled={busy}>
              {busy ? 'Sending…' : 'Send'}
            </button>
          </form>
        )}
        {member && mine?.length > 0 && !ticket && (
          <div className="cs-sw-card" style={{ marginTop: 16 }}>
            <h3>Your requests</h3>
            <ul className="cs-support-list">
              {mine.map((t) => (
                <li key={t.id}>
                  <strong>{t.subject || 'Request'}</strong>
                  <span className="cs-help"> {t.topicLabel || t.status}</span>
                  {t.answer && <p style={{ whiteSpace: 'pre-wrap' }}>{t.answer}</p>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
    </PageWorld>
  );
}
